import React, { useMemo, useState } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ExercisePlayer } from '../components/ExercisePlayer';
import { ExerciseCard } from '../components/ExerciseCard';
import { ExerciseFilters } from '../components/ExerciseFilters';
import { BookIcon, CheckCircleIcon, StarIcon, BarChart2Icon, ClockIcon } from 'lucide-react';
import { Sitemap } from '../components/Sitemap';
const SAMPLE_EXERCISE = {
  questions: [{
    id: 1,
    type: 'multiple-choice' as const,
    question: {
      en: "What's the correct response to 'How are you?'",
      ar: "ما هي الإجابة الصحيحة على 'How are you?'"
    },
    options: [{
      en: "I'm fine, thank you",
      ar: 'أنا بخير، شكراً لك'
    }, {
      en: 'Good morning',
      ar: 'صباح الخير'
    }, {
      en: 'Goodbye',
      ar: 'مع السلامة'
    }, {
      en: "You're welcome",
      ar: 'العفو'
    }],
    correctAnswer: "I'm fine, thank you",
    explanation: {
      en: "When someone asks 'How are you?', the most common and polite response is 'I'm fine, thank you.'",
      ar: "عندما يسألك شخص ما 'How are you?', الإجابة الأكثر شيوعاً وتهذيباً هي 'I'm fine, thank you.'"
    }
  }, {
    id: 2,
    type: 'fill-blank' as const,
    question: {
      en: "Complete the sentence: 'She ___ to school every day.' (Use 'go' in present tense)",
      ar: "أكمل الجملة: 'She ___ to school every day.' (استخدم 'go' في زمن المضارع)"
    },
    correctAnswer: 'goes',
    explanation: {
      en: "With third-person singular subjects (he/she/it), we add 's' to the base form of the verb in present simple tense.",
      ar: "مع ضمائر الغائب المفرد (he/she/it)، نضيف 's' إلى الفعل الأساسي في زمن المضارع البسيط."
    }
  }]
};
const EXERCISES = [{
  title: {
    en: 'Basic Grammar',
    ar: 'القواعد الأساسية'
  },
  description: {
    en: 'Practice fundamental English grammar concepts',
    ar: 'تدرب على مفاهيم قواعد اللغة الإنجليزية الأساسية'
  },
  questions: 20,
  timeEstimate: '15-20',
  difficulty: 'Beginner',
  difficultyAr: 'مبتدئ',
  type: 'Multiple Choice',
  typeAr: 'اختيار من متعدد',
  topic: 'grammar',
  progress: 0,
  streak: 0
}, {
  title: {
    en: 'Everyday Vocabulary',
    ar: 'المفردات اليومية'
  },
  description: {
    en: 'Learn common words and phrases',
    ar: 'تعلم الكلمات والعبارات الشائعة'
  },
  questions: 25,
  timeEstimate: '20-25',
  difficulty: 'Intermediate',
  difficultyAr: 'متوسط',
  type: 'Fill in the Blanks',
  typeAr: 'املأ الفراغات',
  topic: 'vocabulary',
  progress: 40,
  streak: 3
}, {
  title: {
    en: 'Business Communication',
    ar: 'التواصل التجاري'
  },
  description: {
    en: 'Professional English exercises',
    ar: 'تمارين اللغة الإنجليزية المهنية'
  },
  questions: 30,
  timeEstimate: '25-30',
  difficulty: 'Advanced',
  difficultyAr: 'متقدم',
  type: 'Multiple Choice',
  typeAr: 'اختيار من متعدد',
  topic: 'conversation',
  progress: 75,
  streak: 5
}];
export function PracticeExercises() {
  const {
    isArabic
  } = useLanguage();
  const [selectedExercise, setSelectedExercise] = useState<number | null>(null);
  const [completedExercises, setCompletedExercises] = useState<Set<number>>(new Set());
  const [filters, setFilters] = useState({
    difficulty: [] as string[],
    type: [] as string[],
    topic: [] as string[]
  });
  const handleFilterChange = (category: 'difficulty' | 'type' | 'topic', value: string) => {
    setFilters(prev => ({
      ...prev,
      [category]: prev[category].includes(value) ? prev[category].filter(v => v !== value) : [...prev[category], value]
    }));
  };
  const clearFilters = () => {
    setFilters({
      difficulty: [],
      type: [],
      topic: []
    });
  };
  const filteredExercises = useMemo(() => {
    return EXERCISES.filter(exercise => {
      const matchesDifficulty = filters.difficulty.length === 0 || filters.difficulty.includes(exercise.difficulty.toLowerCase());
      const matchesType = filters.type.length === 0 || filters.type.includes(exercise.type.toLowerCase().replace(/ /g, '-'));
      const matchesTopic = filters.topic.length === 0 || filters.topic.includes(exercise.topic);
      return matchesDifficulty && matchesType && matchesTopic;
    });
  }, [filters]);
  const handleExerciseComplete = (score: number) => {
    if (selectedExercise !== null) {
      setCompletedExercises(new Set([...completedExercises, selectedExercise]));
      setSelectedExercise(null);
    }
  };
  return <div className="w-full px-6 md:px-10 lg:px-20 py-16" data-id="element-3878">
      <Sitemap currentPage={{
      en: 'Practice Exercises',
      ar: 'تمارين تطبيقية'
    }} breadcrumbs={[{
      label: {
        en: 'Learn',
        ar: 'تعلم'
      },
      href: '/learn'
    }]} data-id="element-3879" />
      {selectedExercise !== null ? <ExercisePlayer questions={SAMPLE_EXERCISE.questions} onComplete={handleExerciseComplete} data-id="element-3880" /> : <>
          <ExerciseFilters selectedFilters={filters} onFilterChange={handleFilterChange} onClearFilters={clearFilters} data-id="element-3881" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-3882">
            {filteredExercises.map((exercise, index) => <ExerciseCard key={index} exercise={{
          ...exercise,
          isCompleted: completedExercises.has(index)
        }} onClick={() => setSelectedExercise(index)} data-id="element-3883" />)}
          </div>
          {filteredExercises.length === 0 && <div className="text-center py-12 text-gray-500 dark:text-gray-400" data-id="element-3884">
              {isArabic ? 'لا توجد تمارين تطابق المعايير المحددة' : 'No exercises match the selected criteria'}
            </div>}
        </>}
    </div>;
}